import { useCallback, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { OfficeInfoCollection } from '~/constants/ff.posthog';
import {
  getMissingOfficeInfoFields,
  toOfficeMissingInfoSource,
  useOfficeCredentials,
  useOfficeProfile,
  type IOfficeMissingInfoFormValues,
} from '~/domain/OfficeMissingInfo';
import { useAppSelector } from '~/hooks/useAppSelector';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { useUserSelector } from '~/hooks/useUserSelector';
import type { IUser } from '~/models';
import { toProfessionCode } from '~/utils/profession';
import { usePickedChildOffice } from '../hooks/usePickedChildOffice';
import { type IShiftDetailsFormValues } from '../ShiftDetails/ShiftDetailsForm/useShiftDetailsForm';
import { OfficeMissingInfoModal } from './OfficeMissingInfoModal';
import { useOfficeMissingInfoCatalog } from './useOfficeMissingInfoCatalog';
import { useTrackMissingInfoViewed } from './useTrackMissingInfoViewed';

interface IOfficeMissingInfoGateProps {
  isPaused?: boolean;
}

/**
 * Mounted once in the shift request layout, so it covers every entry point into the wizard.
 * Opens the missing-info modal when the flag is on, a profession is chosen, and the office the request
 * is for lacks a field for it. That office is the store's selected office — or, for a parent viewing as
 * itself, the child it picks in the form's Office dropdown (GOTU-14829): a parent that has picked no
 * office yet never sees the modal.
 */
export const OfficeMissingInfoGate = ({ isPaused = false }: IOfficeMissingInfoGateProps) => {
  const isEnabled = useFeatureFlag(OfficeInfoCollection);
  const { isParentView, pickedChildId } = usePickedChildOffice();
  const storeOffice = useUserSelector() as IUser | null;
  // The office every request is scoped to (`x-office`), i.e. resolveScopedOfficeId() ?? user.id.
  const scopedOfficeId = useAppSelector(
    (state) => state.user.selectedOfficeId ?? state.user.user?.id ?? null,
  );

  const { control, getValues, setValue } = useFormContext<IShiftDetailsFormValues>();
  const professionType = useWatch({ control, name: 'professionType' });
  const professionCode = toProfessionCode(professionType);

  // The child a parent picks in Shift Details' Office dropdown is the office the shift is for, and the
  // one whose details are missing; its profile is read on its own below.
  const targetOfficeId = pickedChildId ?? (isParentView ? null : scopedOfficeId);

  const childQuery = useOfficeProfile({ officeId: pickedChildId, enabled: isEnabled });
  const office = pickedChildId ? (childQuery.data ?? null) : storeOffice;

  // After a failed office switch the store still holds the previous office's profile; never
  // merge that into a save aimed at another office. (A picked child is read by its own id.)
  const isOfficeCurrent =
    !!pickedChildId || (!!storeOffice?.id && storeOffice.id === scopedOfficeId);
  const canCollect = isEnabled && !!targetOfficeId && isOfficeCurrent;

  const credentialsQuery = useOfficeCredentials({ officeId: targetOfficeId, enabled: canCollect });

  const source = useMemo(
    () =>
      office && credentialsQuery.data
        ? toOfficeMissingInfoSource(office, credentialsQuery.data)
        : null,
    [office, credentialsQuery.data],
  );

  const missingFields = useMemo(
    () => (source && professionCode ? getMissingOfficeInfoFields(source, professionCode) : []),
    [source, professionCode],
  );

  const { catalog, isReady: isCatalogReady } = useOfficeMissingInfoCatalog(missingFields);

  // A successful save wrote every asked field. If the re-read after it failed, the fields above
  // are stale; re-asking would re-send operatories, which the server rejects as NothingToUpdate,
  // and trap the office. So a save closes the modal for this office and profession.
  const [savedFor, setSavedFor] = useState<string | null>(null);
  const saveKey = `${targetOfficeId}:${professionCode}`;

  const isOpen =
    !isPaused &&
    canCollect &&
    !!professionCode &&
    credentialsQuery.isSuccess &&
    !!source &&
    missingFields.length > 0 &&
    isCatalogReady &&
    savedFor !== saveKey;

  useTrackMissingInfoViewed(isOpen, { professionType: professionCode, missingFields });

  // The layout seeds `isAssistedHygiene` once, at mount, from the selected office; carry a newly saved
  // answer into it when the wizard had none. For a picked child that seed is the parent's answer, not
  // the child's (GOTU-15825), so the answer just saved for the child always replaces it.
  // The answer is `null` unless assisted hygiene was asked.
  const handleSaved = useCallback(
    ({ isAssistedHygiene }: IOfficeMissingInfoFormValues) => {
      setSavedFor(saveKey);
      if (isAssistedHygiene === null) return;
      if (pickedChildId || getValues('isAssistedHygiene') === undefined) {
        setValue('isAssistedHygiene', isAssistedHygiene);
      }
    },
    [getValues, setValue, saveKey, pickedChildId],
  );

  if (!isOpen || !source || !targetOfficeId) {
    return null;
  }

  return (
    <OfficeMissingInfoModal
      missingFields={missingFields}
      professionCode={professionCode}
      source={source}
      officeId={targetOfficeId}
      isSelectedOffice={!pickedChildId}
      catalog={catalog}
      onSaved={handleSaved}
    />
  );
};
